import { useState, useCallback } from "react";
import { APIClient } from "../services/api/ApiClient";
import { CompassError } from "../types/errorTypes";
import { AxiosRequestConfig } from "axios";
import { useApp } from "../store/AppProvider";
import { API_ROUTES } from "../services/api/config";

interface BaseResponse<T> {
  status: string;
  message: string;
  data?: T;
}

export function usePost<D, P>(): {
  data: D | null;
  error: CompassError | null;
  isLoading: boolean;
  sendData: (
    request: keyof typeof API_ROUTES,
    payload: P,
    config?: Record<string, string>,
    isFileUpload?: boolean
  ) => Promise<D | null>;
} {
  const { app } = useApp();
  const [data, setData] = useState<D | null>(null);
  const [error, setError] = useState<CompassError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getDefaultConfig = (isFileUpload: boolean): AxiosRequestConfig => {
    return {
      headers: {
        token: app.authorisation.token,
        "Content-Type": isFileUpload
          ? "multipart/form-data"
          : "application/json",
      },
    };
  };

  const sendData = useCallback(
    async (
      request: keyof typeof API_ROUTES,
      payload: P,
      config?: AxiosRequestConfig,
      isFileUpload = false
    ): Promise<D | null> => {
      const endpoint = API_ROUTES[request];
      const requestConfig: AxiosRequestConfig = {
        ...getDefaultConfig(isFileUpload),
        ...config,
      };
      setIsLoading(true);
      const apiClient = new APIClient();
      try {
        const response: BaseResponse<D> = await apiClient.post(
          endpoint,
          payload,
          requestConfig
        );
        const responseData = response?.data || null;
        setData(responseData);
        return responseData;
      } catch (err) {
        setError(err as CompassError);
        setData(null);
        return null; // Return null instead of re-throwing the error
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { data, error, isLoading, sendData };
}
